<script setup>
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { DxList } from 'devextreme-vue/list'

import { getI18n } from '@/plugins/i18n'
import { useODataSource } from '@/composables/data-grid'
import { reportPeriodShortLabel, useReportSubject } from '@/composables/report'
import { useHighlightNewRow } from '@/composables/scroll'
import { useAccountingStore } from '@/stores/accounting.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import BottomTabsView from '@/components/shared/BottomTabsView.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import FilterSortSelector from '@/components/shared/FilterSortSelector.vue'
import NavigateBack from '@/components/shared/NavigateBack.vue'
import ReportDetailPanel from '@/components/accounting/ReportDetailPanel.vue'
import ReportDuplicateDialog from '@/components/accounting/ReportDuplicateDialog.vue'
import ReportEdit from '@/components/accounting/ReportEdit.vue'
import ReportFileDialog from '@/components/accounting/ReportFileDialog.vue'
import ReportListItem from '@/components/accounting/ReportListItem.vue'
import ReportRecomputeDialog from '@/components/accounting/ReportRecomputeDialog.vue'
import SignalBar from '@/components/shared/signal-bar/SignalBar.vue'

const i18n = getI18n()
const route = useRoute()
const accountingStore = useAccountingStore()
const webSiteStore = useWebSiteStore()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()
const { subjectLabel } = useReportSubject()
const { reportSubjectEnum } = accountingStore

const KPI_VIEW_KEY = 'reports'

const listContainer = ref(null)
let listInstance = null
const isEmptyDataSource = ref(false)
const detailItem = ref(null)
const editItem = ref(null)
const fileReport = ref(null)
const fileStartStep = ref(accountingStore.reportFileStepEnum.DOCUMENT)
const recomputeItem = ref(null)
const duplicateItem = ref(null)
const deleteItem = ref(null)

const ALL_TAB = 'all'
const subjectTabs = [
  { label: i18n.t('common.all'), name: ALL_TAB },
  { label: i18n.t('common.staff'), name: reportSubjectEnum.STAFF },
  { label: i18n.t('common.company', 2), name: reportSubjectEnum.COMPANY },
]
webSiteStore.fabActions = [{ id: 'add', icon: 'mdi-file-chart-outline', onClick: () => { editItem.value = {} } }]

const dataSource = useODataSource(accountingStore.api.getAbsoluteUrl() + accountingStore.api.reportsUrl, {
  onChanged: () => { isEmptyDataSource.value = dataSource.totalCount() === 0 },
  searchExpr: 'name',
  customParamsFn: () => (webSiteStore.reportsSubjectFilter === null ? null : { subject: webSiteStore.reportsSubjectFilter }),
})

const activeTab = computed(() => webSiteStore.reportsSubjectFilter ?? ALL_TAB)
const onTabChange = (tab) => {
  webSiteStore.reportsSubjectFilter = tab === ALL_TAB ? null : tab
  dataSource.reload()
}

const reloadAsync = () => dataSource.reload()

const onReportSaved = async(report) => {
  const isCreation = !editItem.value?.id
  editItem.value = null
  detailItem.value = null
  if (isCreation) {
    webSiteStore.activateUpdatedDateSort(route.name)
    dataSource.sort(webSiteStore.buildSortOrderBy(route.name))
    dataSource.pageIndex(0)
  }
  await reloadAsync()
  if (isCreation) {
    await highlightRowAsync(report.id, listContainer)
    listInstance?.repaint()
  }
}

const openFile = (report, startStep = accountingStore.reportFileStepEnum.DOCUMENT) => {
  fileStartStep.value = startStep
  fileReport.value = report
}

const detailCaption = computed(() => [subjectLabel(detailItem.value), reportPeriodShortLabel(detailItem.value)].filter(Boolean).join(' · '))

const detailActions = computed(() => [
  { key: 'edit', label: i18n.t('common.edit'), icon: 'mdi-pencil', handler: () => { editItem.value = { ...detailItem.value } } },
  { key: 'print', label: i18n.t('common.print'), icon: 'mdi-printer', handler: () => openFile(detailItem.value) },
  { key: 'excel', label: i18n.t('common.exportToExcel'), icon: 'mdi-microsoft-excel', handler: () => openFile(detailItem.value, accountingStore.reportFileStepEnum.EXCEL) },
  { key: 'recompute', label: i18n.t('common.report.recompute'), icon: 'mdi-refresh', handler: () => { recomputeItem.value = detailItem.value } },
  { key: 'duplicate', label: i18n.t('common.report.duplicate'), icon: 'mdi-file-multiple', handler: () => { duplicateItem.value = detailItem.value } },
  { key: 'delete', label: i18n.t('common.delete'), icon: 'mdi-delete-outline', handler: () => { deleteItem.value = detailItem.value } },
])

const onDuplicated = async() => {
  duplicateItem.value = null
  detailItem.value = null
  await reloadAsync()
}

const onDeleteConfirmed = async() => {
  await accountingStore.deleteReportAsync(deleteItem.value.id)
  deleteItem.value = null
  detailItem.value = null
  await reloadAsync()
}

const onRecomputed = async() => {
  recomputeItem.value = null
  detailItem.value = null
  await reloadAsync()
}
</script>

<template>
  <bottom-tabs-view :model-value="activeTab" :tabs="subjectTabs" @update:model-value="onTabChange">
    <filter-sort-selector
      :data-source="dataSource"
      hide-archived
      :kpi-enabled="true"
      :kpi-visible="webSiteStore.isKpiBarVisible(KPI_VIEW_KEY)"
      @toggle-kpi="webSiteStore.toggleKpiBar(KPI_VIEW_KEY)"
    />
    <signal-bar :page="KPI_VIEW_KEY" embedded :visible="webSiteStore.isKpiBarVisible(KPI_VIEW_KEY)" />
    <q-dialog :model-value="editItem !== null" maximized persistent>
      <report-edit
        v-if="editItem !== null"
        :model-value="editItem"
        @update="onReportSaved"
        @cancel="editItem = null"
      />
    </q-dialog>
    <q-dialog :model-value="fileReport !== null" maximized @hide="fileReport = null">
      <report-file-dialog
        v-if="fileReport !== null"
        :report="fileReport"
        :start-step="fileStartStep"
        @saved="reloadAsync"
        @cancel="fileReport = null"
      />
    </q-dialog>
    <report-duplicate-dialog
      v-if="duplicateItem"
      :report="duplicateItem"
      @duplicated="onDuplicated"
      @cancel="duplicateItem = null"
    />
    <confirm-dialog
      :model-value="deleteItem !== null"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDeleteConfirmed"
      @cancel="deleteItem = null"
    />
    <report-recompute-dialog
      v-if="recomputeItem"
      :report="recomputeItem"
      @recomputed="onRecomputed"
      @cancel="recomputeItem = null"
    />
    <q-dialog :model-value="detailItem !== null" maximized>
      <q-card v-if="detailItem" class="dialog-card-flex">
        <navigate-back :text="detailItem.name" :caption="detailCaption" @cancel="detailItem = null">
          <template #right>
            <q-btn flat round icon="mdi-dots-vertical">
              <q-menu anchor="bottom right" self="top right">
                <q-list style="min-width: 12.5rem">
                  <q-item
                    v-for="action in detailActions"
                    :key="action.key"
                    v-close-popup
                    clickable
                    @click="action.handler()"
                  >
                    <q-item-section avatar><q-icon :name="action.icon" /></q-item-section>
                    <q-item-section>{{ action.label }}</q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
          </template>
        </navigate-back>
        <q-card-section class="col q-pa-none" style="min-height: 0;">
          <report-detail-panel :report="detailItem" @recompute="recomputeItem = detailItem" />
        </q-card-section>
      </q-card>
    </q-dialog>
    <q-card flat class="col q-pa-none" style="min-height: 0;">
      <q-card-section ref="listContainer" class="full-height q-pa-none">
        <dx-list
          v-show="!isEmptyDataSource"
          class="events-list"
          :data-source="dataSource"
          height="100%"
          page-load-mode="scrollBottom"
          item-template="item"
          selection-mode="none"
          :active-state-enabled="false"
          @initialized="e => listInstance = e.component"
        >
          <template #item="{ data: report }">
            <report-list-item :item="report" :class="{ 'row-highlight': report.id === highlightedRowId }" @click="detailItem = report" />
          </template>
        </dx-list>
        <div v-show="isEmptyDataSource" class="empty-container">
          <img src="/img/no-documents.svg" :style="{ maxWidth: '80vw', objectFit: 'contain' }" class="q-mb-xl">
          <p class="text-h5">{{ i18n.t('common.report.empty') }}</p>
          <p class="sub text-weight-light">{{ i18n.t('common.report.emptyHint') }}</p>
        </div>
      </q-card-section>
    </q-card>
  </bottom-tabs-view>
</template>
